import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  // Fade,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { noop } from 'lodash/fp';

const useStyles = makeStyles((theme) => ({
  giveBackBox: {
    minWidth: '50px',
    minHeight: 'auto',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 3,
    borderTopLeftRadius: '16px',
    borderBottomRightRadius: '16px',
  },
  card: {
    position: 'relative',
    borderRadius: '16px',
    width: '100%',
  },
  cardMedia: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    borderRadius: '16px',
  },
  standAloneCard: {
    width: '100%',
    paddingTop: '56.25%', // 16:9 = 9 / 16 = 0.5625
    backgroundSize: 'contain',
  },
  square: {
    borderRadius: '0',
    width: '100%',
  },
}));

const CustomImgCard = ({
  name,
  imageUrl,
  giveBack,
  details,
  elevation,
  variant,
  onClick,
  clickable,
  square,
  rectangle,
  width,
  isLoading,
  ariaLabel,
  styleMedia,
}) => {
  const classes = useStyles();
  const giveBackPercent = `${parseFloat((giveBack * 100).toFixed(2))}%`;
  const [is16by9, setIs16by9] = useState(true);
  useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      const ratio = img.width / img.height;
      const expectedRatio = 16 / 9;
      const isCloseEnough = Math.abs(ratio - expectedRatio) < 0.05;
      setIs16by9(isCloseEnough);
    };
  }, [imageUrl]);
  return (
    <>
      <Card
        variant={variant}
        square={square}
        elevation={elevation}
        className={square ? classes.square : classes.card}
      >
        {giveBack ? (
          <Box className={classes.giveBackBox}>
            <Typography display="block" align="center" variant="subtitle1">
              {giveBackPercent}
            </Typography>
          </Box>
        ) : null}
        {clickable ? (
          <>
            {!imageUrl || isLoading ? (
              <Skeleton variant="rect" className={classes.cardMedia} />
            ) : (
              <CardActionArea aria-label={ariaLabel} onClick={onClick}>
                <CardMedia
                  className={classes.cardMedia}
                  alt={name}
                  image={imageUrl}
                />
              </CardActionArea>
            )}
          </>
        ) : (
          <>
            {!imageUrl || isLoading ? (
              <Skeleton
                variant="rect"
                className={
                  square
                    ? `${classes.standAloneCard} ${classes.square}`
                    : classes.standAloneCard
                }
                style={{
                  paddingTop: !rectangle
                    ? 'calc(56.25% + 19px)'
                    : 'calc(25% + 19px)',
                  width: width || '',
                  minWidth: 300,
                  ...styleMedia,
                }}
              />
            ) : (
              <CardMedia
                component="div"
                className={
                  square
                    ? `${classes.standAloneCard} ${classes.square}`
                    : classes.standAloneCard
                }
                alt={name}
                image={imageUrl}
                style={{
                  paddingTop: rectangle ? '25%' : '56.25%', // 16:9 = 9 / 16 = 0.5625
                  width: width || '100%',
                  ...styleMedia,
                  backgroundSize: is16by9 ? 'contain' : 'cover',
                }}
              />
            )}
          </>
        )}
      </Card>
      {details && (
        <Typography
          display="block"
          align="center"
          variant="body1"
          color="primary"
        >
          {!name || isLoading ? <Skeleton /> : name}
        </Typography>
      )}
    </>
  );
};

CustomImgCard.defaultProps = {
  name: '',
  imageUrl: '',
  giveBack: 0,
  details: false,
  elevation: 3,
  clickable: false,
  variant: 'outlined',
  onClick: noop,
  square: false,
  rectangle: false,
  width: '',
  isLoading: false,
  ariaLabel: 'clickable image',
  styleMedia: null,
};

CustomImgCard.propTypes = {
  name: PropTypes.string,
  imageUrl: PropTypes.string,
  variant: PropTypes.string,
  giveBack: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  details: PropTypes.bool,
  elevation: PropTypes.number,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  square: PropTypes.bool,
  rectangle: PropTypes.bool,
  width: PropTypes.string,
  isLoading: PropTypes.bool,
  ariaLabel: PropTypes.string,
  styleMedia: React.CSSProperties,
};

export default CustomImgCard;
